import type { AvailabilityItem, DeliveryTariff, Nutrition, OrderItemInput, StorefrontItem } from '../api/types.ts';
import { para, type Rsd } from '../core/money.ts';
import { createStore, useStore } from '../core/store.ts';

/**
 * Корзина. Переживает перезапуск: человек собирает заказ по дороге домой и не должен
 * собирать его заново. Цена и название запоминаются при добавлении, но заказ оценивает
 * сервер: пока корзина лежала, витрина могла устареть.
 */
export type CartLine = {
  productId: string;
  /** Обновляется с витрины: после смены языка название приходит на нём. */
  name: string;
  priceRsd: Rsd;
  unit: string;
  photoUrl: string | null;
  qty: number;
  /** Товар 18+: оформление не даёт оставить такой заказ у двери. */
  ageRestricted: boolean;
  /** Вес нетто и пищевая ценность на 100 г: по ним корзина показывает итоги. */
  netWeightG: number | null;
  nutrition: Nutrition | null;
  /** Брутто единицы: вес корзины против лимита заказа. */
  weightG: number | null;
};

/** Что случилось с товаром, пока он лежал в корзине: из этого собираются подсказки и правки. */
export type CartChange =
  | { kind: 'gone'; productId: string; name: string }
  | { kind: 'reduced'; productId: string; name: string; availableQty: number };

export type CartState = {
  lines: CartLine[];
  /** Остаток с последней загрузки витрины: сколько ещё можно добавить. */
  availability: Readonly<Record<string, number>>;
};

const LINES_KEY = 'trotty10.client.cart';
const OWNER_KEY = 'trotty10.client.cart-owner';

function readLines(): CartLine[] {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(LINES_KEY) ?? '[]');
    if (!Array.isArray(parsed)) return [];
    // Корзина, сохранённая прежней версией, читается без новых полей.
    return (parsed as Partial<CartLine>[])
      .filter((line) => typeof line.productId === 'string' && typeof line.qty === 'number')
      .map((line) => ({
        productId: line.productId as string,
        name: line.name ?? '',
        priceRsd: line.priceRsd ?? '0.00',
        unit: line.unit ?? 'pcs',
        photoUrl: line.photoUrl ?? null,
        qty: line.qty as number,
        ageRestricted: line.ageRestricted ?? false,
        netWeightG: line.netWeightG ?? null,
        nutrition: line.nutrition ?? null,
        weightG: line.weightG ?? null,
      }));
  } catch {
    return [];
  }
}

export const cartStore = createStore<CartState>({ lines: readLines(), availability: {} });

function setLines(lines: CartLine[], availability?: Readonly<Record<string, number>>): void {
  cartStore.set((state) => ({ lines, availability: availability ?? state.availability }));
  localStorage.setItem(LINES_KEY, JSON.stringify(lines));
}

function setAvailability(availability: Readonly<Record<string, number>>): void {
  cartStore.set((state) => ({ ...state, availability }));
}

function lineFrom(item: StorefrontItem, qty: number): CartLine {
  return {
    productId: item.id,
    name: item.name,
    priceRsd: item.priceRsd,
    unit: item.unit,
    photoUrl: item.photoUrl,
    qty,
    ageRestricted: item.ageRestricted,
    netWeightG: item.netWeightG,
    nutrition: item.nutrition,
    weightG: item.weightG,
  };
}

export function useCart(): CartState {
  return useStore(cartStore);
}

// Чтение

export function lineTotal(line: CartLine): bigint {
  return para(line.priceRsd) * BigInt(line.qty);
}

export function itemsCount(state: CartState): number {
  return state.lines.reduce((sum, line) => sum + line.qty, 0);
}

export function itemsTotal(state: CartState): bigint {
  return state.lines.reduce((sum, line) => sum + lineTotal(line), 0n);
}

/** Товар этой строки на складе адреса закончился. */
export function isGone(state: CartState, productId: string): boolean {
  const available = state.availability[productId];
  return available !== undefined && available <= 0;
}

/** Сумма строк, которые сервер ещё примет: закончившиеся в итог не входят. */
export function availableTotal(state: CartState): bigint {
  return state.lines.filter((line) => !isGone(state, line.productId)).reduce((sum, line) => sum + lineTotal(line), 0n);
}

/** Брутто строк, которые сервер примет: сравнивается с лимитом веса заказа. */
export function availableWeightG(state: CartState): number {
  return state.lines.filter((line) => !isGone(state, line.productId)).reduce((sum, line) => sum + (line.weightG ?? 0) * line.qty, 0);
}

/** Брутто всей корзины: оформление отправляет все строки. */
export function cartWeightG(state: CartState): number {
  return state.lines.reduce((sum, line) => sum + (line.weightG ?? 0) * line.qty, 0);
}

/** Вес заказа с лимитом, когда он превышен; null, пока корзина помещается. */
export function overweight(tariff: DeliveryTariff | null | undefined, weightG: number): { actual: number; limit: number } | null {
  const limit = tariff?.maxOrderWeightG ?? 0;
  return limit > 0 && weightG > limit ? { actual: weightG, limit } : null;
}

export function qtyOf(state: CartState, productId: string): number {
  return state.lines.find((line) => line.productId === productId)?.qty ?? 0;
}

/** Неизвестный остаток не ограничивает: заказ всё равно проверит сервер. */
export function canAdd(state: CartState, productId: string): boolean {
  const available = state.availability[productId];
  return available === undefined || qtyOf(state, productId) < available;
}

export function hasAgeRestricted(state: CartState): boolean {
  return state.lines.some((line) => line.ageRestricted);
}

export function hasGoneLines(state: CartState): boolean {
  return state.lines.some((line) => isGone(state, line.productId));
}

/** Правки, которых требует последний известный остаток. Пустой список — корзина в порядке. */
export function pendingChanges(state: CartState): CartChange[] {
  const changes: CartChange[] = [];
  for (const line of state.lines) {
    const available = state.availability[line.productId];
    if (available === undefined) continue;
    if (available <= 0) changes.push({ kind: 'gone', productId: line.productId, name: line.name });
    else if (available < line.qty) {
      changes.push({ kind: 'reduced', productId: line.productId, name: line.name, availableQty: available });
    }
  }
  return changes;
}

/** Строки для оформления заказа. */
export function orderItems(state: CartState): OrderItemInput[] {
  return state.lines.map((line) => ({ productId: line.productId, qty: line.qty }));
}

// Правка

/**
 * Корзина принадлежит человеку, а не устройству: при входе она сверяется с тем, кто её собрал.
 * Собранная гостем корзина хозяина не имеет и достаётся вошедшему; чужая (другой номер после
 * потерянной сессии) выбрасывается раньше, чем её кто-то увидит.
 */
export function adoptCart(userId: string): void {
  const owner = localStorage.getItem(OWNER_KEY);
  if (owner && owner !== userId) setLines([], {});
  localStorage.setItem(OWNER_KEY, userId);
}

/** Выход: корзина пустеет и никому не принадлежит, следующий гость начинает с чистой. */
export function releaseCart(): void {
  clearCart();
  localStorage.removeItem(OWNER_KEY);
}

export function addToCart(item: StorefrontItem): void {
  const { lines } = cartStore.get();
  const existing = lines.find((line) => line.productId === item.id);
  setLines(
    existing
      ? lines.map((line) => (line.productId === item.id ? { ...line, qty: line.qty + 1 } : line))
      : [...lines, lineFrom(item, 1)],
  );
}

/**
 * Несколько строк с заданным количеством разом: повтор прошлого заказа. Количество
 * прибавляется к лежащему и ограничивается известным остатком. Возвращает то, что не
 * поместилось или закончилось.
 */
export function addAllToCart(items: { item: StorefrontItem; qty: number }[]): CartChange[] {
  const skipped: CartChange[] = [];
  let { lines } = cartStore.get();
  const availability = { ...cartStore.get().availability };

  for (const { item, qty } of items) {
    availability[item.id] = item.availableQty;
    if (item.availableQty <= 0) {
      skipped.push({ kind: 'gone', productId: item.id, name: item.name });
      continue;
    }
    const current = lines.find((line) => line.productId === item.id)?.qty ?? 0;
    const target = Math.min(current + qty, item.availableQty);
    if (target < current + qty) {
      skipped.push({ kind: 'reduced', productId: item.id, name: item.name, availableQty: target });
    }
    lines = lines.some((line) => line.productId === item.id)
      ? lines.map((line) =>
          line.productId === item.id ? { ...line, qty: target, priceRsd: item.priceRsd, photoUrl: item.photoUrl } : line,
        )
      : target > 0
        ? [...lines, lineFrom(item, target)]
        : lines;
  }

  setLines(lines, availability);
  return skipped;
}

export function decreaseInCart(productId: string): void {
  const { lines } = cartStore.get();
  const line = lines.find((entry) => entry.productId === productId);
  if (!line) return;
  setLines(
    line.qty > 1
      ? lines.map((entry) => (entry.productId === productId ? { ...entry, qty: entry.qty - 1 } : entry))
      : lines.filter((entry) => entry.productId !== productId),
  );
}

export function removeFromCart(productId: string): void {
  setLines(cartStore.get().lines.filter((line) => line.productId !== productId));
}

export function clearCart(): void {
  setLines([], {});
}

/**
 * Сверка с ответом о наличии: цена обновляется, остаток запоминается. Количество в строках
 * здесь не меняется — это делает `applyCartChanges`.
 */
export function applyAvailability(items: AvailabilityItem[]): CartChange[] {
  const state = cartStore.get();
  const availability = { ...state.availability };
  let lines = state.lines;
  let linesChanged = false;

  for (const item of items) {
    availability[item.productId] = item.availableQty;
    if (lines.some((line) => line.productId === item.productId && line.priceRsd !== item.priceRsd)) {
      lines = lines.map((line) => (line.productId === item.productId ? { ...line, priceRsd: item.priceRsd } : line));
      linesChanged = true;
    }
  }

  if (linesChanged) setLines(lines, availability);
  else setAvailability(availability);
  return pendingChanges(cartStore.get());
}

/**
 * Остаток из ленты событий: кеш наличия правится без запроса. Лента несёт остатки всего
 * склада, поэтому товар, которого нет ни в строках, ни в кеше, пропускается.
 */
export function applyStock(productId: string, availableQty: number): void {
  const state = cartStore.get();
  const known = state.availability[productId];
  if (known === undefined && !state.lines.some((line) => line.productId === productId)) return;
  if (known === availableQty) return;
  setAvailability({ ...state.availability, [productId]: availableQty });
}

/** Убрать закончившееся и подрезать то, чего осталось меньше. */
export function applyCartChanges(changes: CartChange[]): void {
  let { lines } = cartStore.get();
  const availability = { ...cartStore.get().availability };
  for (const change of changes) {
    if (change.kind === 'gone') {
      lines = lines.filter((line) => line.productId !== change.productId);
    } else {
      lines = lines.map((line) => (line.productId === change.productId ? { ...line, qty: change.availableQty } : line));
      availability[change.productId] = change.availableQty;
    }
  }
  setLines(lines, availability);
}

/**
 * Сверка с витриной: пока корзина лежит, остатки кончаются, а цены меняются.
 *
 * Отсутствие товара в ответе ничего не значит: витрина приходит страницами и фильтруется
 * поиском. Чужой складу товар сервер отклонит при оформлении.
 */
export function syncCartWith(items: StorefrontItem[]): void {
  if (items.length === 0) return;
  const state = cartStore.get();
  const byId = new Map(items.map((item) => [item.id, item]));

  let availability = state.availability;
  for (const item of byId.values()) {
    if (availability[item.id] !== item.availableQty) {
      if (availability === state.availability) availability = { ...availability };
      (availability as Record<string, number>)[item.id] = item.availableQty;
    }
  }

  let changed = false;
  const lines = state.lines.map((line) => {
    const item = byId.get(line.productId);
    if (!item) return line;
    if (
      line.name === item.name &&
      line.priceRsd === item.priceRsd &&
      line.photoUrl === item.photoUrl &&
      line.netWeightG === item.netWeightG &&
      line.weightG === item.weightG &&
      JSON.stringify(line.nutrition) === JSON.stringify(item.nutrition)
    ) {
      return line;
    }
    changed = true;
    return { ...line, name: item.name, priceRsd: item.priceRsd, photoUrl: item.photoUrl, netWeightG: item.netWeightG, nutrition: item.nutrition, weightG: item.weightG };
  });

  if (changed) setLines(lines, availability);
  else if (availability !== state.availability) setAvailability(availability);
}

/** Смена адреса меняет склад: прежние остатки к нему не относятся. */
export function forgetAvailability(): void {
  setAvailability({});
}
